import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { GetPrivateRegionStatsNodesListRes } from '@/api/type-response'
import { message } from '@/components/base/Notification'
import { PrivateRegionNodeType } from '@/consts/privateRegion'

const PAGE_SIZE = 10

export function useComputeNodeData(
  selectedRegion: number | null,
  filters: { gpuModel?: string | number },
) {
  const { t } = useTranslation('privateRegion')
  const [computeNodes, setComputeNodes] = useState<GetPrivateRegionStatsNodesListRes | null>(null)
  const [models, setModels] = useState<string[] | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  // Load models once when region changes
  const loadModels = useCallback(
    async (signal: AbortSignal) => {
      if (selectedRegion === null) return

      try {
        const res = await API.getPrivateRegionModels(
          {
            region: selectedRegion,
            type: PrivateRegionNodeType.AllNode,
          },
          { signal },
        )

        if (res.code === 0) {
          setModels(res.models || [])
        }
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') return
      }
    },
    [selectedRegion],
  )

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      if (selectedRegion === null) return

      setLoading(true)
      try {
        const res = await API.getPrivateRegionNodesList(
          {
            page,
            per_page: PAGE_SIZE,
            region: selectedRegion,
            type: PrivateRegionNodeType.AllNode,
            model: filters.gpuModel ? String(filters.gpuModel) : undefined,
          },
          { signal },
        )

        if (res.code === 0) {
          setComputeNodes(res)
        }
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') return
        message.error(t('errors.failedToLoadComputeNodes'))
      } finally {
        setLoading(false)
      }
    },
    [page, filters, selectedRegion, t],
  )

  // Load models when region changes
  useEffect(() => {
    if (selectedRegion === null) return

    const abortController = new AbortController()
    loadModels(abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [selectedRegion, loadModels])

  // Load node data when region, page, or filters change
  useEffect(() => {
    if (selectedRegion === null) return

    const abortController = new AbortController()
    loadData(abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [selectedRegion, page, filters, loadData])

  const refetch = useCallback(async () => {
    if (selectedRegion === null) return

    try {
      const res = await API.getPrivateRegionNodesList({
        page,
        per_page: PAGE_SIZE,
        region: selectedRegion,
        type: PrivateRegionNodeType.AllNode,
        model: filters.gpuModel ? String(filters.gpuModel) : undefined,
      })

      if (res.code === 0) {
        setComputeNodes(res)
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') return
    }
  }, [selectedRegion, page, filters])

  return {
    computeNodes,
    models,
    page,
    setPage,
    loading,
    refetch,
    pageSize: PAGE_SIZE,
  }
}
